import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { AuthRedirectDebugService } from '../auth-redirect-debug.service';
import { AuthRedirectReason } from '../auth-redirect-reason';
import { AuthSessionPolicy } from '../models/auth-session-policy.model';
import { formatRefreshError } from '../utils/auth-token-diagnostics.util';
import { AuthSessionService } from './auth-session.service';
import { KeycloakClientService } from './keycloak-client.service';

@Injectable({
    providedIn: 'root',
})
export class AuthTokenService {
    private updateTokenPromise: Promise<boolean> | null = null;

    constructor(
        private keycloakClient: KeycloakClientService,
        private authSession: AuthSessionService,
        private authRedirectDebug: AuthRedirectDebugService,
        private logger: LoggerService
    ) {}

    async updateToken(minValidity?: number): Promise<boolean> {
        if (!this.keycloakClient.isInitialized()) {
            throw new Error('Keycloak client is not initialized.');
        }

        if (this.updateTokenPromise) {
            this.logger.debug('[AuthToken] Reusing in-flight refresh promise');
            return this.updateTokenPromise;
        }

        const validity = minValidity ?? this.keycloakClient.getUpdateMinValidity();

        this.updateTokenPromise = this.doUpdateToken(validity).finally(() => {
            this.updateTokenPromise = null;
        });

        return this.updateTokenPromise;
    }

    async ensureTokenFresh(): Promise<boolean> {
        if (!this.keycloakClient.isInitialized() || !this.keycloakClient.isLoggedIn()) {
            return false;
        }

        try {
            await this.updateToken();
            return this.keycloakClient.isLoggedIn();
        } catch (error) {
            this.logger.warn(
                '[AuthToken] ensureTokenFresh failed',
                JSON.stringify({
                    ...(await this.buildFailureContext(undefined, error)),
                })
            );
            return false;
        }
    }

    async ensureTokenFreshIfNearExpiry(): Promise<boolean> {
        if (!this.keycloakClient.isInitialized() || !this.keycloakClient.isLoggedIn()) {
            return false;
        }

        if (!this.keycloakClient.isTokenNearExpiry()) {
            return true;
        }

        return this.ensureTokenFresh();
    }

    private async doUpdateToken(minValidity: number): Promise<boolean> {
        const diagnostics = this.keycloakClient.getRefreshDiagnostics(minValidity);

        if (diagnostics.isNearExpiry) {
            this.logger.debug(
                '[AuthToken] Entering refresh window, calling keycloak.updateToken',
                JSON.stringify(diagnostics)
            );
        }

        this.authRedirectDebug.markRefreshAttempt();

        try {
            const refreshed = await this.keycloakClient.updateToken(minValidity);

            if (refreshed) {
                this.logger.debug(
                    '[AuthToken] Token refreshed via Keycloak',
                    JSON.stringify(this.keycloakClient.getRefreshDiagnostics(minValidity))
                );
            } else if (diagnostics.isNearExpiry) {
                this.logger.debug(
                    '[AuthToken] updateToken returned false near expiry (no network refresh)',
                    JSON.stringify(this.keycloakClient.getRefreshDiagnostics(minValidity))
                );
            }

            this.authRedirectDebug.markRefreshSuccess();
            return refreshed;
        } catch (error) {
            this.authRedirectDebug.markRefreshError();

            const failureContext = await this.buildFailureContext(minValidity, error);
            this.logger.warn(
                '[AuthToken] Token refresh failed',
                JSON.stringify(failureContext)
            );

            this.authSession.handleSessionEnded(
                AuthRedirectReason.TokenRefreshFailed,
                AuthSessionPolicy.ReLogin,
                { refreshFailure: failureContext }
            );
            throw new Error('Failed to refresh the token, or the session is expired');
        }
    }

    private async buildFailureContext(
        minValidity: number | undefined,
        error: unknown
    ): Promise<Record<string, unknown>> {
        return {
            tokenState: this.keycloakClient.getRefreshDiagnostics(minValidity),
            ...(await formatRefreshError(error)),
        };
    }
}
